import React, {useCallback, useEffect, useRef, useState} from "react";
import {Container, Card, Form} from "react-bootstrap";
import "./ReportSection.css";
import axios from "../../util/Axios/AxiosInstance";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import "bootstrap/dist/css/bootstrap.min.css";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import * as ReactBootStrap from "react-bootstrap";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import {format} from "date-fns";
import AOS from "aos";
import "aos/dist/aos.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const SKELETON_ROW_COUNT = 8;
const REFRESH_INTERVAL_MS = 2 * 60 * 1000;
const REFRESH_START_HOUR = 9;
const REFRESH_END_HOUR = 18;

const isWithinRefreshHours = (date = new Date()) => {
    const hour = date.getHours();
    return hour >= REFRESH_START_HOUR && hour < REFRESH_END_HOUR;
};

const isPageVisible = () =>
    typeof document === "undefined" || document.visibilityState === "visible";

const canAutoRefresh = () => isWithinRefreshHours() && isPageVisible();

const SKELETON_FIELDS = [
    "logDateTime", "employeeCode", "employeeName", "departmentName", "deviceLocation",
];

const createSkeletonRows = () =>
    Array.from({length: SKELETON_ROW_COUNT}, (_, index) => ({
        id: `skeleton-${index}`,
        sNo: index + 1,
        ...Object.fromEntries(SKELETON_FIELDS.map((field) => [field, <Skeleton key={field} />])),
    }));

function LiveLog() {
    const [tableData, setTableData] = useState(() => createSkeletonRows());
    const [formatDate, setFormatDate] = useState("");
    const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [withinRefreshHours, setWithinRefreshHours] = useState(() => isWithinRefreshHours());
    const [pageVisible, setPageVisible] = useState(() => isPageVisible());
    const abortControllerRef = useRef(null);

    const fetchLiveLogs = useCallback((date, background = false) => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;

        if (background) {
            setRefreshing(true);
        } else {
            setLoading(true);
            setTableData(createSkeletonRows());
        }

        axios.post("vms/liveLogs", {
            date,
            plantCode: localStorage.getItem("plantCode"),
        }, {signal: controller.signal})
            .then((response) => {
                const payload = response?.data;
                if (!payload || payload.status === "failed") {
                    setTableData([]);
                    setTotalCount(0);
                    return;
                }

                const records = Array.isArray(payload.data) ? payload.data : [];
                setTotalCount(payload.totalCount ?? records.length);
                setTableData(
                    records.filter(Boolean).map((record, index) => ({
                        ...record,
                        id: `live-log-${index}`,
                        sNo: index + 1,
                    }))
                );
            })
            .catch((error) => {
                if (error.code === "ERR_CANCELED") {
                    return;
                }
                console.error(error);
                if (!background) {
                    setTableData([]);
                    setTotalCount(0);
                }
            })
            .finally(() => {
                setLoading(false);
                setRefreshing(false);
            });
    }, []);

    useEffect(() => {
        AOS.init();
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const today = new Date();
        setFormatDate(`${months[today.getMonth()]}${today.getDate()},${today.getFullYear()}`);
    }, []);

    useEffect(() => {
        fetchLiveLogs(selectedDate);
    }, [selectedDate, fetchLiveLogs]);

    useEffect(() => {
        const tick = () => {
            if (canAutoRefresh()) {
                fetchLiveLogs(selectedDate, true);
            }
        };

        const intervalId = setInterval(tick, REFRESH_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [selectedDate, fetchLiveLogs]);

    useEffect(() => {
        const syncRefreshWindow = () => setWithinRefreshHours(isWithinRefreshHours());
        syncRefreshWindow();
        const intervalId = setInterval(syncRefreshWindow, 60 * 1000);
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            const visible = isPageVisible();
            setPageVisible(visible);
            if (visible && isWithinRefreshHours()) {
                fetchLiveLogs(selectedDate, true);
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    }, [selectedDate, fetchLiveLogs]);

    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const formatDateTime = (cell) => {
        if (!cell || typeof cell !== "string") {
            return "";
        }
        return cell.replace("T", " ");
    };

    const formatDateTimeShort = (cell) => {
        const full = formatDateTime(cell);
        if (!full) {
            return "";
        }
        const [datePart, timePart = ""] = full.split(" ");
        const dateSegments = datePart.split("-");
        if (dateSegments.length === 3) {
            const [year, month, day] = dateSegments;
            const [hour = "00", minute = "00", second = "00"] = timePart.split(":");
            return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
        }
        return full;
    };

    const dateTimeFormatter = (cell) => (
        <span title={formatDateTime(cell)}>{formatDateTimeShort(cell)}</span>
    );

    const textCellFormatter = (cell) => (
        <span title={cell ?? ""}>{cell ?? ""}</span>
    );

    const dateTimeCsvFormatter = (cell) => formatDateTimeShort(cell) || formatDateTime(cell) || "";

    const headerStyle = (minWidth) => ({
        width: minWidth,
        minWidth,
        textAlign: "left",
    });

    const colStyle = (minWidth) => ({
        minWidth,
        style: {minWidth},
    });

    const withDateExport = (column) => ({
        ...column,
        csvFormatter: dateTimeCsvFormatter,
    });

    const columns = [
        {
            dataField: "sNo",
            text: "S No.",
            formatter: (cell, row, rowIndex) => (pageNumber - 1) * pageSize + (rowIndex + 1),
            headerStyle: () => headerStyle("55px"),
            style: () => ({...colStyle("55px"), textAlign: "center"}),
        },
        withDateExport({
            dataField: "logDateTime",
            text: "Log Time",
            sort: true,
            formatter: dateTimeFormatter,
            headerStyle: () => headerStyle("155px"),
            style: () => colStyle("155px"),
        }),
        {
            dataField: "employeeCode",
            text: "Emp Code",
            sort: true,
            formatter: textCellFormatter,
            headerStyle: () => headerStyle("95px"),
            style: () => colStyle("95px"),
        },
        {
            dataField: "employeeName",
            text: "Emp Name",
            sort: true,
            formatter: textCellFormatter,
            headerStyle: () => headerStyle("130px"),
            style: () => colStyle("130px"),
        },
        {
            dataField: "departmentName",
            text: "Department",
            sort: true,
            formatter: textCellFormatter,
            headerStyle: () => headerStyle("120px"),
            style: () => colStyle("120px"),
        },
        {
            dataField: "deviceLocation",
            text: "Device Location",
            sort: true,
            formatter: textCellFormatter,
            headerStyle: () => headerStyle("140px"),
            style: () => colStyle("140px"),
        },
    ];

    const paginationOptions = {
        sizePerPage: pageSize,
        onSizePerPageChange: (sizePerPage) => setPageSize(sizePerPage),
        onPageChange: (page) => setPageNumber(page),
    };

    const escapeCsvValue = (value) => {
        const str = value == null ? "" : String(value);
        if (/[",\n]/.test(str)) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const getExportCellValue = (row, column, rowIndex) => {
        if (column.dataField === "sNo") {
            return rowIndex + 1;
        }
        const raw = row[column.dataField];
        if (column.csvFormatter) {
            return column.csvFormatter(raw, row, rowIndex);
        }
        if (raw == null || typeof raw === "object") {
            return "";
        }
        return raw;
    };

    const exportToCsv = () => {
        if (!tableData.length) {
            return;
        }

        const headerRow = columns.map((column) => escapeCsvValue(column.text)).join(",");
        const dataRows = tableData.map((row, rowIndex) =>
            columns.map((column) => escapeCsvValue(getExportCellValue(row, column, rowIndex))).join(",")
        );

        const csvContent = [headerRow, ...dataRows].join("\n");
        const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `LiveLog ${selectedDate}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const getAutoRefreshStatus = () => {
        if (refreshing) {
            return " · Refreshing...";
        }
        if (!withinRefreshHours) {
            return " · Auto-refresh paused (outside 9 AM – 6 PM)";
        }
        if (!pageVisible) {
            return " · Auto-refresh paused (tab in background)";
        }
        return " · Auto-refreshes every 2 minutes (9 AM – 6 PM)";
    };

    return (
        <Card className="cardStyl">
            <Container fluid className="px-0">
                <div className="mt-2">
                    <h5 className="priColor d-flex align-items-center gap-2">
                        Live Log
                        {(loading || refreshing) && (
                            <ReactBootStrap.Spinner animation="border" size="sm" role="status">
                                <span className="sr-only">Loading...</span>
                            </ReactBootStrap.Spinner>
                        )}
                    </h5>
                    {!loading && (
                        <p className="text-muted mb-0 small">
                            {totalCount} record(s) for {selectedDate}
                            {getAutoRefreshStatus()}
                        </p>
                    )}
                </div>
                <div className="subDiv mt-2">
                    <Form.Group className="mb-0">
                        <Form.Label className="me-2 mb-0">Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            style={{width: "180px", display: "inline-block"}}
                        />
                    </Form.Group>
                </div>
                <div className="mt-3">
                    <ToolkitProvider keyField="id" data={tableData} columns={columns}>
                        {(props) => (
                            <>
                                {!loading && tableData.length > 0 && (
                                    <div className="exportButton">
                                        <button className="btn btn-success" onClick={exportToCsv}>
                                            Export to CSV !!
                                        </button>
                                    </div>
                                )}
                                <div
                                    data-aos="zoom-in-right"
                                    data-aos-duration="550"
                                    className="attendance-table-scroll attendance-table-scroll--live-log"
                                    style={{opacity: loading ? 0.7 : 1}}
                                >
                                    <BootstrapTable
                                        bootstrap4
                                        noDataIndication={loading ? "Loading live logs..." : "No data available"}
                                        pagination={paginationFactory(paginationOptions)}
                                        wrapperClasses="table-responsive attendance-report-table attendance-report-table--live-log"
                                        {...props.baseProps}
                                    />
                                </div>
                            </>
                        )}
                    </ToolkitProvider>
                </div>
            </Container>
        </Card>
    );
}

export default LiveLog;
